import { useState, useCallback } from "react";
import { API } from "../App";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { MapPin, Search, Loader2 } from "lucide-react";

const GeocodingInput = ({ 
  value, 
  onChange, 
  onLocationSelect,
  placeholder = "Cerca località...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(value || "");

  const searchLocation = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/geocode`, { query });
      setResults(response.data);
      if (response.data.length > 0) {
        setIsOpen(true);
      }
    } catch (error) {
      console.error("Geocoding error:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    onChange(newValue);
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      searchLocation(newValue);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  };

  const handleSelectLocation = (result) => {
    setSearchQuery(result.display_name.split(",")[0]);
    onChange(result.display_name.split(",")[0]);
    onLocationSelect(result.latitude, result.longitude, result.display_name);
    setIsOpen(false);
    setResults([]);
  };

  return (
    <Popover open={isOpen && results.length > 0} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className={`relative ${className}`}>
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={handleInputChange}
            placeholder={placeholder}
            className="pl-10 pr-10"
            data-testid="geocoding-input"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <div className="max-h-60 overflow-y-auto">
          {results.map((result, index) => (
            <button
              key={index}
              className="w-full px-4 py-3 text-left hover:bg-secondary/50 transition-colors border-b border-border last:border-0"
              onClick={() => handleSelectLocation(result)}
              data-testid={`geocoding-result-${index}`}
            >
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">
                    {result.display_name.split(",")[0]}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {result.display_name}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono mt-1">
                    {result.latitude.toFixed(4)}, {result.longitude.toFixed(4)}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default GeocodingInput;
